
import { useRef, useEffect, useState } from 'react'

import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import get from 'lodash/get'

import classNames from 'classnames'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, EffectFade } from 'swiper'

import 'swiper/scss'
import 'swiper/scss/navigation'
import 'swiper/scss/effect-fade'

import styles from './product_slider.module.sass'

import BlockWrapper from './layout/block_wrapper'
import Media from './basic/media'
import Button from './basic/button'
import ScrollAnimation from './layout/scroll_animation'

function formatSlug(slug){
	const words = slug.split('-');

	for (let i = 0; i < words.length; i++) {
		const word = words[i];
	  words[i] = word.charAt(0).toUpperCase() + word.slice(1);
	}
  
	return words.join(' ');
}

function ProductSlider(props) {
	const fields = props.fields

	const productSliderRef = useRef(null)
	const contentRef = useRef(null)
	const titleRef = useRef(null)
	const imageRef = useRef(null)

	const [contentSlides, setContentSlides] = useState([])
	const [imageSlides, setImageSlides] = useState([])
	const [titleSlides, setTitleSlides] = useState([])

	const isMobile = () => {
		if (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) < 768)
			return true
		return false
	}

	const imageSwiperConfig = {
		modules: [EffectFade],
		spaceBetween: 0,
		slidesPerView: 1,
		observer: false,
		loop: false,
		effect: isMobile() ? '' : 'fade',
		fadeEffect: {
			crossFade: true
		},
		breakpoints: {
			0: {
				slidesPerView: 1,
				spaceBetween: 10
			},
			768: {
				slidesPerView: 1,
				spaceBetween: 10,
			}
		},
		onSwiper: (swiper) => {
			imageRef.current = swiper
		},
		onUpdate: (swiper) => {
			swiper.slideTo(0)
			swiper.updateSlidesClasses()
		},
		onSlideChange: (swiper) => {
			contentRef.current.slideTo(swiper.realIndex)
			titleRef.current.slideTo(swiper.realIndex)
		},
	}

	const titleSwiperConfig = {
		modules: [EffectFade],
		loop: false,
		slidesPerView: 'auto',
		slidesPerGroup: 1,
		centeredSlides: false,
		observer: false,
		slideToClickedSlide: true,
		onSwiper: (swiper) => {
			titleRef.current = swiper
		},
		onSlideChange: (swiper) => {
			swiper.snapGrid = swiper.slidesGrid
			contentRef.current.slideTo(swiper.realIndex)
			imageRef.current.slideTo(swiper.realIndex)
		},
		onSnapGridLengthChange: (swiper) => {
			//hack to reset snap grid
			swiper.snapGrid = swiper.slidesGrid
		},
		onUpdate: (swiper) => {
			swiper.snapGrid = swiper.slidesGrid
			swiper.slideTo(0)
			swiper.updateSlidesClasses()
		}
	}

	const swiperConfig = {
		modules: [Navigation, EffectFade],
		navigation: {
			prevEl: `.${styles.previous}`,
			nextEl: `.${styles.next}`,
			clickable: true
		},
		spaceBetween: 0,
		slidesPerView: 1,
		loop: false,
		effect: 'fade',
		fadeEffect: {
			crossFade: true
		},
		onSwiper: (swiper) => {
			contentRef.current = swiper
		},
		onSlideChange: (swiper) => {
			titleRef.current.slideTo(swiper.realIndex)
			imageRef.current.slideTo(swiper.realIndex)
		},
		onUpdate: (swiper) => {
			swiper.slideTo(0)
			swiper.updateSlidesClasses()
		}
	}

	const productCategories = map(uniqBy(fields.slides, (o) => o.category), function (slide) {
		return {'cat' : formatSlug(slide.category), 'slug' : slide.category}
	})
	
	function filterProducts(event, filter){
		const activeFilter = event.target
		const allFilters = Array.from(activeFilter.parentElement.children)

		allFilters.forEach((filter) => {
			filter.classList.remove(styles.active)
		})

		activeFilter.classList.add(styles.active)

		updateSliders([
			{ref: contentRef, slides: contentSlides},
			{ref: imageRef, slides: imageSlides},
			{ref: titleRef, slides: titleSlides}
		], filter)
	}

	function updateSliders(sliderData, filter){
		sliderData.map((slider) => {
			slider.slides.forEach((slide) => slide.remove())

			let filteredSlides = slider.slides.filter((slide) => slide.getAttribute('data-category').indexOf(filter) > -1)
			
			filteredSlides.forEach((slide) => {
				slider.ref.current.el.childNodes[0].appendChild(slide)
			})

			slider.ref.current.update()
		})
	}

	useEffect(() => {
		setContentSlides([...get(contentRef, 'current.el.childNodes[0].childNodes', [])])
		setImageSlides([...get(imageRef, 'current.el.childNodes[0].childNodes', [])])
		setTitleSlides([...get(titleRef, 'current.el.childNodes[0].childNodes', [])])
	}, [])

	return <BlockWrapper {...props} className={styles.product_slider}>
		<ScrollAnimation styles={styles}>
			<div className={styles.product_slider_container}>
				<div className={styles.product_filter}>
					{productCategories.map((category, index) => {
						return <button key={index} className={classNames(styles.h3, styles.category_filter)} onClick={(event) => filterProducts(event, category.slug)}>{category.cat}</button>
					})}
				</div>
				
				<div className={styles.grid}>
					<div ref={productSliderRef} className={styles.product_slider_wrapper}>

						<div className={styles.navigation}>
							<button aria-label='previous' className={styles.previous}></button>
							<button aria-label='next' className={styles.next}></button>
						</div>

						<Swiper ref={titleRef} className={styles.product_title_slides}  {...titleSwiperConfig}>
							{fields.slides.map((product, index) => {

							return <SwiperSlide className={styles.product_title_slide} key={index} data-category={product.category}>
								<button className={styles.h3}>{product.title}</button>
							</SwiperSlide>
							})}
						</Swiper>

						<Swiper ref={contentRef} className={styles.product_slides} {...swiperConfig}>
							{fields.slides.map((product, index) => {
								return <SwiperSlide className={styles.product_slide} key={index} data-category={product.category}>
									<div className={styles.product_content_wrapper}>
										<div className={styles.product_content} dangerouslySetInnerHTML={{__html: product.content}}></div>
										<Button fields={{button: product.button}} className={styles.button}></Button>
									</div>
								</SwiperSlide>
							})}
						</Swiper>
					</div>

					<div className={styles.product_image_wrapper}>
						<Swiper ref={imageRef} className={styles.product_image_slides} {...imageSwiperConfig}>
							{fields.slides.map((product, index) => {
								return <SwiperSlide className={styles.product_image_slide} key={index} data-category={product.category}>
									<Media {...product.media} className={styles.product_image} settings={{texture: true}} />
								</SwiperSlide>
							})}
						</Swiper>
					</div>
				</div>
			</div>
		</ScrollAnimation>
	</BlockWrapper>
}

export default ProductSlider